import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getStoredToken, clearStoredTokens } from '../login/api/loginApi';
import { isTokenExpired } from './auth/jwt';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Protects routes that require authentication.
 *
 * - No token in storage → redirect to /login?returnUrl=...
 * - Token present but expired (FE exp check) → clear tokens + redirect with
 *   expired=1 flag so LoginPage can display the session-expired banner.
 *
 * Note: FE expiry check is UX-only. BE JwtAuthenticationFilter remains the
 * authoritative gate. The authInterceptor covers the case where a token
 * expires mid-session (after the guard passed).
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const location = useLocation();
  const token = getStoredToken();

  if (!token) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  if (isTokenExpired(token)) {
    clearStoredTokens();
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?expired=1&returnUrl=${returnUrl}`} replace />;
  }

  return <>{children}</>;
}
